import React from "react";
import { useTranslation } from "react-i18next";
import {
  SecondaryCriteriaTemplate,
  SecondaryCriteria,
  ResourcesListingConfiguration,
} from "@planzone/dto";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { DragAndDrop, Draggable, DropBox } from "@common/dragAndDrop";
import { CrossIcon } from "@common/crossIcon";
import { Props } from "./types";
import { ColumnIcon } from "./columnIcon";
import { Form } from "./form";
import styles from "./styles.scss";

const fixedColumns = [
  "tags",
  "role",
  "invitationStatus",
  "subordinates",
  "organizationalUnit",
];

function hasAvailableOptions(
  template: SecondaryCriteriaTemplate,
  profile: ResourcesListingConfiguration[number],
) {
  return ![...template.map(({ uuid }) => uuid), ...fixedColumns].every(
    (key) => !!profile.columns.find((column) => column.key === key),
  );
}

function Columns({ formData, edit }: Props) {
  const { t, i18n } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "resource",
  );
  const currentLang =
    ["fr", "en"].find((lang) => new RegExp(lang, "i").test(i18n.language)) ||
    "fr";
  const getName = (key: string, criteria?: SecondaryCriteria) =>
    criteria
      ? criteria.labels[currentLang as keyof typeof criteria.labels]
        ? criteria.labels[currentLang as keyof typeof criteria.labels]
        : criteria.name
      : t(`resourcesListingConfiguration.columnNames.${key}`, key);

  return (
    <DragAndDrop>
      <div className={styles.columns}>
        {formData.columns
          .filter(({ key }) =>
            [...fixedColumns, ...template.map(({ uuid }) => uuid)].find(
              (k) => k === key,
            ),
          )
          .map(({ key }, index, keys) => {
            const criteria = template.find(({ uuid }) => uuid === key);

            return (
              <React.Fragment key={key}>
                <DropBox
                  acceptedTypes={["MOVE_COLUMN"]}
                  horizontalSlop={20}
                  verticalSlop={20}
                  drop={({ payload }) => {
                    const droppedIndex = keys.findIndex(
                      ({ key }) => key === payload,
                    );
                    const nextColumns = [
                      ...keys.slice(0, index),
                      keys[droppedIndex],
                      keys[index],
                      ...keys.slice(index + 1),
                    ];

                    edit({
                      ...formData,
                      columns: [
                        ...nextColumns.slice(
                          0,
                          droppedIndex + (index < droppedIndex ? 1 : 0),
                        ),
                        ...nextColumns.slice(
                          droppedIndex + (index < droppedIndex ? 2 : 1),
                        ),
                      ],
                    });
                  }}
                >
                  {({ payload }, canDrop) => {
                    const criteria = template.find(
                      ({ uuid }) => uuid === payload,
                    );

                    return (
                      canDrop && (
                        <div className={styles.column}>
                          <ColumnIcon
                            criteriaKey={payload}
                            className={styles.columnIcon}
                          />
                          <p>{getName(payload, criteria)}</p>
                        </div>
                      )
                    );
                  }}
                </DropBox>
                <Draggable
                  type="MOVE_COLUMN"
                  className={styles.columnWrapper}
                  data={key}
                  draggedClassName={styles.hidden}
                >
                  <div className={styles.column}>
                    <ColumnIcon
                      criteriaKey={key}
                      className={styles.columnIcon}
                    />
                    <p>{getName(key, criteria)}</p>
                    <div
                      className={styles.iconBox}
                      onClick={() =>
                        edit({
                          ...formData,
                          columns: [
                            ...keys.slice(0, index),
                            ...keys.slice(index + 1),
                          ],
                        })
                      }
                    >
                      <CrossIcon className={styles.icon} />
                    </div>
                  </div>
                </Draggable>
                <DropBox
                  acceptedTypes={["MOVE_COLUMN"]}
                  horizontalSlop={20}
                  verticalSlop={20}
                  drop={({ payload }) => {
                    const droppedIndex = keys.findIndex(
                      ({ key }) => key === payload,
                    );
                    const nextColumns = [
                      ...keys.slice(0, index),
                      keys[index],
                      keys[droppedIndex],
                      ...keys.slice(index + 1),
                    ];

                    edit({
                      ...formData,
                      columns: [
                        ...nextColumns.slice(
                          0,
                          droppedIndex + (index < droppedIndex ? 1 : 0),
                        ),
                        ...nextColumns.slice(
                          droppedIndex + (index < droppedIndex ? 2 : 1),
                        ),
                      ],
                    });
                  }}
                >
                  {({ payload }, canDrop) => {
                    const criteria = template.find(
                      ({ uuid }) => uuid === payload,
                    );

                    return (
                      canDrop && (
                        <div className={styles.column}>
                          <ColumnIcon
                            criteriaKey={payload}
                            className={styles.columnIcon}
                          />
                          <p>{getName(payload, criteria)}</p>
                        </div>
                      )
                    );
                  }}
                </DropBox>
              </React.Fragment>
            );
          })}
        {hasAvailableOptions(template, formData) && (
          <div className={styles.columnWrapper}>
            <Form
              lang={currentLang as "fr" | "en"}
              formData={formData}
              edit={edit}
            />
          </div>
        )}
      </div>
    </DragAndDrop>
  );
}

export { Columns };
